import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { BackArrow, Button, Input, Select, TopProgress, Typography, CheckBox } from '../../../../components'
import {
  Container,
  Header,
  PageBody,
  Form,
  FormSection,
  SectionHeader,
  FieldsGrid,
  ThreeColumnGrid,
  WideField,
  Checkboxes,
  ButtonsWrapper,
} from './styles'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { clientService } from '../../../../api'
import { useUi } from '../../../../context/ui.context'
import { usePageTitle } from '../../../../context/navigationContext'

const normalizeCustomFields = (customFields:any) => {
  if(Array.isArray(customFields)){
    return customFields
  }

  if(!customFields || typeof customFields !== 'object'){
    return []
  }

  return Object.entries(customFields).map(([name, field]:[string, any]) => ({
    ...(field && typeof field === 'object' ? field : {}),
    name:field?.name ?? name,
    label:field?.label ?? (typeof field === 'string' ? field : name),
    value:field?.value ?? '',
  }))
}

const CreateClient:FC = () => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const {viewAlert} = useUi()
  usePageTitle(t("PAGE_TITLE_CLIENT_CREATE"))
  const [pageloading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)
  const [countries, setCountries] = useState<any[]>([])
  const [currencies, setCurrencies] = useState<any[]>([])
  const [defaultCurrency, setDefaultCurrency] = useState<string>('')
  const [configurations, setConfigurations] = useState<any[]>([])
  const [groups, setGroups] = useState<any[]>([])
  const [customFields, setCustomFields] = useState<any[]>([])
  const [euCountries, setEuCountries] = useState<string[]>([])
  const [taxExemptAutomationOptions, setTaxExemptAutomationOptions] = useState<any[]>([])

  useEffect(() => {
    clientService.getOptions()
    .then((res) => {
      const list = res.data.countries || []
      const index = list.findIndex((it:any) => it.value === 'UA')
      const item = list.find((it:any) => it.value === 'UA')
      if(index !== -1 && item){
        list.splice(index, 1)
        list.unshift(item)
      }
      setCountries(list)
      setCurrencies((res.data.currencies || []).map((currency:string) => ({
        id:currency,
        title:currency,
        label:currency,
      })))
      setDefaultCurrency(res.data.default_currency || '')
      setConfigurations((res.data.configurations || []).map((configuration:any) => ({
        ...configuration,
        title:configuration.name,
        label:configuration.name,
      })))
      setCustomFields(normalizeCustomFields(res.data.custom_fields))
      setEuCountries(res.data.eu_countries || [])
      setTaxExemptAutomationOptions(Object.entries(res.data.tax_exempt_automation || {}).map(([id, title]) => ({
        id,
        title,
        label:title,
      })))
    })
    .catch(() => {
      setCountries([])
    })
    .finally(() => setPageLoading(false))

    clientService.getClientGroups({page:1, page_size:1000})
    .then((res) => {
      setGroups((res.data.objects || []).map((group:any) => ({
        ...group,
        label:group.name,
      })))
    })
    .catch(() => setGroups([]))
  },[])

  useEffect(() => {
    if(!values.currency && defaultCurrency){
      setFieldValue('currency', defaultCurrency)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[defaultCurrency])

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      firstName: yup.string().required(`${t('REQUIRED_FIELD')}`),
      lastName: yup.string().required(`${t('REQUIRED_FIELD')}`),
      country: yup.string().required(`${t('REQUIRED_FIELD')}`),
      configuration: yup.string().nullable(),
      currency: yup.string().required(`${t('REQUIRED_FIELD')}`),
      phone: yup.string().required(`${t('REQUIRED_FIELD')}`),
      company: yup.string(),
      vat_id: yup.string(),
      address1: yup.string().required(`${t('REQUIRED_FIELD')}`),
      address2: yup.string(),
      city: yup.string().required(`${t('REQUIRED_FIELD')}`),
      state: yup.string().required(`${t('REQUIRED_FIELD')}`),
      zip_code: yup.string().required(`${t('REQUIRED_FIELD')}`),
      fax: yup.string().max(64),
      external_billing_id: yup.string().max(38),
      auto_order_service_external_billing_id: yup.string().max(38),
      tax_exempt_automation: yup.string().max(16),
    }),
    initialValues: {
      firstName:'',
      lastName:'',
      country:'',
      configuration:configurations.find(configuration => configuration.is_default)?.id ?? '',
      company:'',
      phone:'',
      fax:'',
      vat_id:'',
      tax_exempt:false,
      tax_exempt_automation:'auto',
      validate_phone_number:false,
      do_not_process:false,
      address1:'',
      address2:'',
      city:'',
      state:'',
      zip_code:'',
      currency:defaultCurrency,
      groups: [],
      external_billing_id:'',
      create_auto_order_service:configurations.find(configuration => configuration.is_default)?.auto_create_order ?? false,
      auto_order_service_external_billing_id:'',
      custom_fields:customFields.map((field:any) => ({
        name:field.name,
        label:field.label ?? field.name,
        value:field.value ?? '',
      })),
    },
    onSubmit: (vals:any) => {
      setLoading(true)
      const groupsPayload = vals.groups.map((group:any) => (
        typeof group === 'string' ? group : group.name ?? group.value ?? group.label
      ))
      const data:any = {
        address1: vals.address1 || "",
        address2: vals.address2 || "",
        city: vals.city || "",
        company:vals?.company,
        country:vals.country,
        custom_fields:vals.custom_fields.map((field:any) => ({
          name:field.name,
          value:field.value || '',
        })),
        first_name:vals.firstName,
        last_name:vals.lastName,
        phone:vals?.phone,
        fax:vals.fax || null,
        state:vals.state || "",
        vat_id:vals?.vat_id || "",
        zip_code:vals.zip_code || "",
        tax_exempt: vals.tax_exempt,
        validate_phone_number:vals.validate_phone_number,
        do_not_process:vals.do_not_process,
        configuration: vals.configuration || null,
        currency: vals.currency,
        groups: groupsPayload,
        external_billing_id:vals.external_billing_id || null,
        create_auto_order_service:vals.create_auto_order_service,
        auto_order_service_external_billing_id:vals.create_auto_order_service
          ? vals.auto_order_service_external_billing_id || null
          : null,
      }

      const selectedConfiguration = configurations.find(configuration => configuration.id === vals.configuration)
      const euCountrySelected = euCountries.includes(vals.country)

      if(selectedConfiguration?.auto_eu_tax_exemption && euCountrySelected){
        data.tax_exempt_automation = vals.tax_exempt_automation
      }else if(!euCountrySelected){
        data.tax_exempt_automation = 'auto'
      }

      clientService.createClient(data)
      .then(() => {
        setLoadingComplete(true)
        setTimeout(() => {
          setLoading(false)
          setLoadingComplete(false)
          navigate('/clients-users/clients')
        }, 800)
      })
      .catch((e:any) => {
        if(e?.response?.data?.detail){
          viewAlert({
            severity:'error',
            message:e.response.data.detail,
          })
        }
        setLoading(false)
      })
    },
  })

  const handleGroupChange = (group:any) => {
    const groupName = group.name ?? group.value ?? group.label
    const isSelected = values.groups.some((selectedGroup:any) => (
      (selectedGroup.name ?? selectedGroup.value ?? selectedGroup.label) === groupName
    ))

    setFieldValue(
      'groups',
      isSelected
        ? values.groups.filter((selectedGroup:any) => (
            (selectedGroup.name ?? selectedGroup.value ?? selectedGroup.label) !== groupName
          ))
        : [...values.groups, group]
    )
  }

  const handleConfigurationChange = (configurationId:any) => {
    const configuration = configurations.find(item => item.id === configurationId)

    setFieldValue('configuration', configurationId)
    setFieldValue('create_auto_order_service', Boolean(configuration?.auto_create_order))

    if(!configuration?.auto_eu_tax_exemption){
      setFieldValue('tax_exempt_automation', 'auto')
      setFieldValue('tax_exempt', false)
    }
  }

  const handleCountryChange = (countryCode:string) => {
    setFieldValue('country', countryCode)

    if(!euCountries.includes(countryCode)){
      setFieldValue('tax_exempt_automation', 'auto')
      setFieldValue('tax_exempt', false)
    }
  }

  const handleTaxExemptAutomationChange = (value:string) => {
    setFieldValue('tax_exempt_automation', value)

    if(value === 'always'){
      setFieldValue('tax_exempt', true)
    }else{
      setFieldValue('tax_exempt', false)
    }
  }

  const selectedConfiguration = configurations.find(configuration => configuration.id === values.configuration)
  const showTaxExemptAutomation = Boolean(
    selectedConfiguration?.auto_eu_tax_exemption && euCountries.includes(values.country)
  )

  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  }

  if(pageloading) return <TopProgress loading={pageloading}/>
  return (
    <Container>
      <TopProgress loading={pageloading}/>
      <Header>
        <BackArrow fallbackPath="/clients-users/clients"/>
        <Typography variant='h1'>
          {t("CREATE_CLIENTS")}
        </Typography>
      </Header>
      <PageBody>
        <Form>
          <FormSection>
            <SectionHeader>
              <Typography variant='h3'>{t("GENERAL_INFORMATION")}</Typography>
            </SectionHeader>
            <FieldsGrid>
              <Input
                required
                fieldName='firstName'
                title={t("FIRST_NAME")}
                placeholder=''
                {...formikProps}
              />
              <Input
                required
                fieldName='lastName'
                title={t("LAST_NAME")}
                placeholder=''
                {...formikProps}
              />
              <WideField>
                <Input
                  fieldName='company'
                  title={t("COMPANY")}
                  placeholder=''
                  {...formikProps}
                />
              </WideField>
              <Select
                toolTip={false}
                data={configurations}
                selectedValue={values['configuration']}
                onChange={handleConfigurationChange}
                title={t("CONFIGURATION")}
                placeholder={t("SELECT_CONFIGURATION")}
                hasSearch
                fieldName='configuration'
                {...formikProps}
              />
              <Select
                required
                toolTip={false}
                data={currencies}
                selectedValue={values['currency']}
                onChange={(e:string) => setFieldValue('currency', e)}
                title={t("DEFAULT_CURRENCY")}
                placeholder={t("SELECT_CURRENCY")}
                hasSearch
                fieldName='currency'
                {...formikProps}
              />
              {groups.length > 0 ?
                <WideField>
                  <Select
                    toolTip={false}
                    data={groups}
                    selectedValue={values['groups']}
                    onChange={() => {}}
                    multi
                    multipleOnChange={handleGroupChange}
                    title={t("GROUPS")}
                    placeholder={t("SELECT_GROUPS")}
                    hasSearch
                    fieldName='groups'
                    {...formikProps}
                  />
                </WideField>
              : null}
            </FieldsGrid>
          </FormSection>

          <FormSection>
            <SectionHeader>
              <Typography variant='h3'>{t("ADDRESS")}</Typography>
            </SectionHeader>
            <FieldsGrid>
              <WideField>
                <Select
                  required
                  toolTip={false}
                  data={countries}
                  selectedValue={values['country']}
                  onChange={handleCountryChange}
                  title={t("COUNTRY")}
                  hasSearch
                  fieldName='country'
                  {...formikProps}
                />
              </WideField>
              <WideField>
                <Input
                  required
                  fieldName='address1'
                  title={t("ADDRESS_LINE_1")}
                  placeholder=''
                  {...formikProps}
                />
              </WideField>
              <WideField>
                <Input
                  fieldName='address2'
                  title={t("ADDRESS_LINE_2")}
                  placeholder=''
                  {...formikProps}
                />
              </WideField>
            </FieldsGrid>
            <ThreeColumnGrid>
              <Input
                required
                fieldName='state'
                title={t("COUNTRY_STATE")}
                placeholder=''
                {...formikProps}
              />
              <Input
                required
                fieldName='city'
                title={t("CITY")}
                placeholder=''
                {...formikProps}
              />
              <Input
                required
                fieldName='zip_code'
                title={t("ZIP_CODE")}
                placeholder=''
                {...formikProps}
              />
            </ThreeColumnGrid>
          </FormSection>

          <FormSection $fullWidth>
            <SectionHeader>
              <Typography variant='h3'>{t("CONTACT_AND_BILLING")}</Typography>
            </SectionHeader>
            <FieldsGrid>
              <Input
                required
                fieldName='phone'
                title={t("PHONE")}
                placeholder=''
                phoneNumber
                {...formikProps}
              />
              <Input
                fieldName='fax'
                title={t("FAX")}
                placeholder=''
                {...formikProps}
              />
              <Input
                fieldName='vat_id'
                title={t("VAT_ID")}
                placeholder=''
                {...formikProps}
              />
              <Input
                fieldName='external_billing_id'
                title={t("EXTERNAL_BILLING_ID")}
                placeholder=''
                {...formikProps}
              />
              {showTaxExemptAutomation ?
                <Select
                  toolTip={false}
                  data={taxExemptAutomationOptions}
                  selectedValue={values.tax_exempt_automation}
                  onChange={handleTaxExemptAutomationChange}
                  title={t("TAX_EXEMPT_AUTOMATION")}
                />
              : null}
              <Checkboxes>
                <CheckBox
                  title={t("VALIDATE_PHONE_NUMBER")}
                  value={values.validate_phone_number}
                  onChange={() => setFieldValue('validate_phone_number', !values.validate_phone_number)}
                />
                {!showTaxExemptAutomation || values.tax_exempt_automation !== 'auto' ?
                  <CheckBox
                    title={t("TAX_EXEMPT")}
                    value={values.tax_exempt}
                    disabled={showTaxExemptAutomation}
                    onChange={() => {
                      if(!showTaxExemptAutomation){
                        setFieldValue('tax_exempt', !values.tax_exempt)
                      }
                    }}
                  />
                : null}
                <CheckBox
                  title={t("DO_NOT_PROCESS")}
                  value={values.do_not_process}
                  onChange={() => setFieldValue('do_not_process', !values.do_not_process)}
                />
              </Checkboxes>
            </FieldsGrid>
          </FormSection>

          {selectedConfiguration?.auto_create_order ?
            <FormSection $fullWidth={values.custom_fields.length === 0}>
              <SectionHeader>
                <Typography variant='h3'>{t("CLIENT_AUTOMATION")}</Typography>
              </SectionHeader>
              <CheckBox
                title={selectedConfiguration.auto_order_product_display || t("CREATE_AUTO_ORDER_SERVICE")}
                value={values.create_auto_order_service}
                onChange={() => setFieldValue('create_auto_order_service', !values.create_auto_order_service)}
              />
              {values.create_auto_order_service ?
                <Input
                  fieldName='auto_order_service_external_billing_id'
                  title={t("AUTO_ORDER_SERVICE_EXTERNAL_BILLING_ID")}
                  placeholder=''
                  {...formikProps}
                />
              : null}
            </FormSection>
          : null}
          {values.custom_fields.length > 0 ?
            <FormSection $fullWidth={!selectedConfiguration?.auto_create_order}>
              <SectionHeader>
                <Typography variant='h3'>{t("CUSTOM_FIELDS")}</Typography>
              </SectionHeader>
              <FieldsGrid>
                {values.custom_fields.map((field:any, index:number) => (
                  <Input
                    key={field.name}
                    fieldName={`custom_fields[${index}].value`}
                    title={field.label}
                    value={field.value}
                    handleChange={(event:any) => setFieldValue(`custom_fields[${index}].value`, event.target.value)}
                    placeholder=''
                  />
                ))}
              </FieldsGrid>
            </FormSection>
          : null}
          <ButtonsWrapper>
            <Button
              variant="primary"
              size="display"
              onClick={handleSubmit}
              loading={loading}
              loadingComplete={loadingComplete}
              title={t("CREATE")}
            />
            <Button
              variant="stroke"
              size="display"
              onClick={() => navigate('/clients-users/clients')}
              title={t("CANCEL")}
            />
          </ButtonsWrapper>
        </Form>
      </PageBody>
    </Container>
  )
}

export default CreateClient
